import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import type { ApiResponse } from '@/types'

/** 请求配置（基于 axios，保留 skipInterceptor、body 以兼容 http.post 等） */
export interface RequestConfig extends AxiosRequestConfig {
  /** 是否跳过响应拦截器（直接返回原始 data） */
  skipInterceptor?: boolean
  /** 业务失败时不弹出全局错误提示（仍抛出 ApiError） */
  skipErrorMessage?: boolean
  /** 请求体（与 axios 的 data 等价，用于 http.post/put/patch 的 body 参数） */
  body?: unknown
  /** 请求方法（显式声明便于 IDE 解析） */
  method?: string
  /** 请求体 / 数据（显式声明便于 IDE 解析） */
  data?: unknown
  /** 超时时间（毫秒）（显式声明便于 IDE 解析） */
  timeout?: number
}

/** 文件上传配置（支持上传进度等） */
export interface UploadConfig extends Omit<RequestConfig, 'body' | 'data'> {
  /** 上传进度回调 (loaded, total) */
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
}

/** 文件下载配置 */
export interface DownloadConfig extends Omit<RequestConfig, 'body' | 'data'> {
  /** 响应类型，默认 'blob' */
  responseType?: 'blob' | 'arraybuffer'
  /** 超时时间（毫秒） */
  timeout?: number
}

/** 请求上下文（供拦截器使用） */
export interface RequestContext {
  url: string
  config: AxiosRequestConfig
}

/** 响应上下文（供拦截器使用） */
export interface ResponseContext<T = unknown> {
  data: T
  response: AxiosResponse<T>
  config: RequestConfig
}

/** API 错误 */
export class ApiError extends Error {
  constructor(
    message: string,
    public code?: number,
    public status?: number,
    public data?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** 业务成功码（与后端约定一致；常见为 0 或 200） */
export const SUCCESS_CODE = 0

/** 视为业务成功的响应码列表 */
export const BUSINESS_SUCCESS_CODES: readonly number[] = [0, 200]

export function isBusinessSuccessCode(code: number): boolean {
  return BUSINESS_SUCCESS_CODES.includes(code)
}

/** 从 ApiResponse 取提示文案（兼容 message / msg） */
export function getApiMessage(value: unknown): string {
  if (!isApiResponse(value)) return '请求失败'
  const r = value as ApiResponse<unknown>
  const text = r.message ?? r.msg
  if (typeof text === 'string' && text.trim()) return text.trim()
  return '请求失败'
}

/** 判断是否为标准 ApiResponse 结构 */
export function isApiResponse<T>(value: unknown): value is ApiResponse<T> {
  return typeof value === 'object' && value !== null && 'code' in value && 'data' in value
}
